import ScrollReveal from "@/components/base/ScrollReveal";

import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";
const steps = [
  {
    num: "01",
    icon: "ri-user-search-line",
    title: "Choose your therapist or explore your options",
    body: "Browse therapist profiles to find the right fit for you, or take your time and explore. Each therapist brings a distinct approach, specialty, and style so you can choose with confidence.",
    detail: "Browse at your own pace",
    iconColor: "#7D54FC",
    iconBg: "rgba(125,84,252,0.1)",
    iconBorder: "rgba(125,84,252,0.2)",
    detailColor: "#7D54FC",
  },
  {
    num: "02",
    icon: "ri-calendar-2-line",
    title: "Book your session online",
    body: "Schedule directly through our online booking system at a time that works for you. No back-and-forth required. In-person and telehealth appointments are available.",
    detail: "In-person & telehealth available",
    iconColor: "#3C6FF8",
    iconBg: "rgba(60,111,248,0.1)",
    iconBorder: "rgba(60,111,248,0.2)",
    detailColor: "#3C6FF8",
  },
  {
    num: "03",
    icon: "ri-seedling-line",
    title: "Begin therapy with clear direction and support",
    body: "Your first session is focused and purposeful. You'll leave with a clear sense of what you're working on and a path forward tailored to your goals.",
    detail: "Ongoing support · Flexible scheduling",
    iconColor: "#6B742F",
    iconBg: "rgba(107,116,47,0.1)",
    iconBorder: "rgba(107,116,47,0.2)",
    detailColor: "#6B742F",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="w-full scroll-mt-[108px] py-14 md:scroll-mt-[112px] md:py-20"
      style={{
        background:
          "linear-gradient(160deg, #FBF9F6 0%, #F8F4EC 55%, #F5F1E8 100%)",
      }}
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="max-w-xl mb-14 md:mb-16">
          <ScrollReveal variant="fadeIn" delay={0}>
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-5 h-[2px] rounded-full"
                style={{
                  background: "linear-gradient(90deg, #6B742F, #3C6FF8)",
                }}
              />
              <span
                className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                style={{ color: "#6B742F" }}
              >
                Getting Started Is Simple
              </span>
            </div>
          </ScrollReveal>

          <ScrollReveal variant="fadeUp" delay={80}>
            <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug">
              Three steps to your
              <br />
              <em className="not-italic" style={{ color: "#3C6FF8" }}>
                first session
              </em>
            </h2>
          </ScrollReveal>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
          <div
            className="hidden md:block absolute top-8 left-[33%] right-[33%] h-px"
            style={{
              background: "linear-gradient(90deg, #B4A6E3 0%, #3C6FF8 100%)",
              opacity: 0.35,
            }}
          ></div>

          {steps.map((s, i) => (
            <ScrollReveal key={s.num} variant="fadeUp" delay={i * 120} threshold={0.1}>
              <div
                className="relative flex flex-col h-full rounded-2xl p-6 border"
                style={{ background: s.iconBg, borderColor: s.iconBorder }}
              >
                {/* Colored left accent bar */}
                <div
                  className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
                  style={{ background: s.iconColor }}
                />

                <div className="flex items-center gap-4 mb-6 pl-3">
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-full relative z-10 border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.8)",
                      borderColor: s.iconBorder,
                    }}
                  >
                    <i className={`${s.icon} text-xl`} style={{ color: s.iconColor }}></i>
                  </div>

                  <span
                    className="font-manrope text-[12px] font-semibold tracking-widest uppercase"
                    style={{ color: s.iconColor }}
                  >
                    Step {s.num}
                  </span>
                </div>

                <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium leading-snug mb-3 pl-3">
                  {s.title}
                </h3>

                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-4 flex-1 pl-3">
                  {s.body}
                </p>

                <div className="inline-flex items-center gap-1.5 pl-3">
                  <i className="ri-time-line text-sm" style={{ color: s.detailColor }}></i>
                  <span
                    className="font-manrope text-[13px] font-medium"
                    style={{ color: s.detailColor }}
                  >
                    {s.detail}
                  </span>
                </div>

                {i < steps.length - 1 && (
                  <div className="md:hidden flex justify-start mt-6 pl-3">
                    <i className="ri-arrow-down-line text-xl" style={{ color: "#CBBFAF" }}></i>
                  </div>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* CTA */}
        <ScrollReveal variant="fadeUp" delay={120}>
          <div className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <a
              href={ACTIVE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap btn-primary"
              style={{ background: "#3C6FF8" }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8")
              }
            >
              Book Now <i className="ri-arrow-right-line"></i>
            </a>

            <a
              href="/therapists"
              className="inline-flex items-center gap-1.5 font-manrope text-[15px] font-medium text-[#2B2B2B]/78 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap link-arrow"
            >
              Find Your Therapist <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
